/**
 * The value mark: who decided this value (docs/9-exploration/SPEC-value-mark-icon.md,
 * owner rulings 2026-10-16). One small icon, five states, drawn beside every
 * official value an AI can judge — criterion values, and an item's category and
 * altitude. This file turns a value's history into the state and the exact
 * tooltip; components/ds/ValueMark draws it. Every surface goes through here so
 * the words are the same on the item page, the item list and the agent report.
 *
 *   human          a person set it and no AI was involved
 *   ai_approved    an AI recommended it and a person approved it
 *   ai_rule        an AI decided it through a standing rule a person set
 *   ai_overridden  a person replaced or changed an AI's value or recommendation (R-4)
 *   ai_waiting     an AI recommends a value and no person has acted yet
 *
 * R-3: no mark at all on a value no AI can judge (its AI setting is off and no
 * AI ever proposed it).
 */

import { formatDate } from '@/i18n/formatters';
import type { ItemClassificationProposal } from '@/types/prioritization/itemClassification';
import { isAgentProposer, type ValueAiSetting, type ValueStatus, type ValueStatusResult } from './valueStatus';
import type { ValueProposal } from '../types/prioritization/itemLifecycle';

export type ValueMarkState = Exclude<ValueStatus, 'none'>;

export interface ValueMarkView {
  state: ValueMarkState;
  /** The tooltip, exactly as the spec words it. */
  tip: string;
  /** A newer AI recommendation waiting while this value stands (both are shown). */
  waitingTip: string | null;
}

export interface ValueMarkNames {
  /** A person's display name from their id. */
  nameOf: (id: string | null | undefined) => string | null;
  /** An agent's display name from its type (value_estimator → Value Estimator). */
  agentName: (agentType: string | null | undefined) => string;
}

const show = (v: unknown): string => {
  if (v == null || v === '') return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};
const day = (d: Date | string | null | undefined) => (d ? formatDate(d) : null);
const by = (name: string | null, at: Date | string | null | undefined) =>
  [name ?? 'someone', day(at)].filter(Boolean).join(', ');

function proposalAgent(p: ValueProposal | null | undefined, names: ValueMarkNames): string {
  if (!p) return 'The agent';
  if (p.proposedByName) return p.proposedByName;
  return names.agentName(isAgentProposer(p.proposedBy) ? p.proposedBy.slice('agent:'.length) : null);
}

/** True when an AI can judge this value: its AI setting is on, or an AI has ever proposed it (R-3). */
export function aiCanJudgeValue(
  itemId: string,
  attributeKey: string,
  setting: ValueAiSetting | null | undefined,
  proposals: ValueProposal[],
): boolean {
  if (setting && setting.mode !== 'off') return true;
  return proposals.some((p) => p.itemId === itemId && p.attributeKey === attributeKey && isAgentProposer(p.proposedBy));
}

/** The mark for a criterion value, from valueStatus(). Null when there is nothing to mark. */
export function valueMarkFor(
  result: ValueStatusResult,
  setting: ValueAiSetting | null | undefined,
  aiCanJudge: boolean,
  names: ValueMarkNames,
): ValueMarkView | null {
  if (result.status === 'none' || !aiCanJudge) return null;
  const waitingTip =
    result.accepted && result.waiting
      ? `${proposalAgent(result.waiting, names)} recommends ${show(result.waiting.proposedValue)} · waiting for a person`
      : null;
  const a = result.accepted;
  switch (result.status) {
    case 'human':
      return { state: 'human', tip: `Human · set by ${by(names.nameOf(a?.acceptedBy), a?.acceptedAt)}`, waitingTip };
    case 'ai_approved':
      return { state: 'ai_approved', tip: `AI recommended · approved by ${by(names.nameOf(a?.acceptedBy), a?.acceptedAt)}`, waitingTip };
    case 'ai_rule': {
      const setter = names.nameOf(setting?.setBy) ?? names.nameOf(a?.acceptedBy) ?? 'an admin';
      const on = day(setting?.setAt);
      return { state: 'ai_rule', tip: `AI decided · by the rule ${setter} set${on ? ` on ${on}` : ''}`, waitingTip };
    }
    case 'ai_overridden': {
      const o = result.override;
      const who = by(names.nameOf(o?.by), o?.at);
      const agent = proposalAgent(o?.agentProposal, names);
      const tip =
        o?.kind === 'changed_before_accepting'
          ? `Changed by ${who} · ${agent} recommended ${show(o.agentValue)}`
          : `Changed by ${who} · replaced ${agent}'s ${show(o?.agentValue)}`;
      return { state: 'ai_overridden', tip, waitingTip };
    }
    case 'ai_waiting':
      return {
        state: 'ai_waiting',
        tip: `${proposalAgent(result.waiting, names)} recommends ${show(result.waiting?.proposedValue)} · waiting for a person`,
        waitingTip: null,
      };
  }
}

// ── Category and altitude (item_classification_proposals) ────────────────────

export type ClassificationField = 'category' | 'altitude';

const fieldValue = (p: ItemClassificationProposal, f: ClassificationField) =>
  f === 'category' ? p.classificationFields.category : p.classificationFields.altitudeLevel;
/** When the row's decision (or, unaccepted, its proposal) happened. */
const when = (p: ItemClassificationProposal) => new Date(p.acceptedAt ?? p.createdAt).getTime();
/** Did this row set the field? A person's own entry always does; an AI proposal when that field was accepted. */
const decides = (p: ItemClassificationProposal, f: ClassificationField) =>
  p.status === 'accepted' &&
  (p.proposedBy === 'user' || !p.acceptedFields || (f === 'category' ? p.acceptedFields.category : p.acceptedFields.altitude));

/**
 * The mark for an item's category or altitude, from its classification history
 * (spec §3.2). An AI proposal a person accepted → approved; a person's own entry
 * that changed an AI's recommendation (the open proposal it superseded) or
 * replaced an AI value that stood → overridden; otherwise human. A declined
 * field of a partly accepted proposal is not a decision on that field.
 * Classification has no standing rule, so never ai_rule.
 */
export function classificationMark(
  itemId: string,
  field: ClassificationField,
  history: ItemClassificationProposal[],
  aiCanJudge: boolean,
  names: ValueMarkNames,
  fieldLabel: (v: unknown) => string = show,
): ValueMarkView | null {
  const rows = history.filter((p) => p.itemId === itemId).sort((a, b) => when(b) - when(a));
  const isAgent = (p: ItemClassificationProposal) => p.proposedBy === 'agent';
  const agentOf = (p: ItemClassificationProposal) => names.agentName(p.proposedByAgent ?? null);
  const open = rows.find((p) => isAgent(p) && p.status === 'active') ?? null;
  const openTip = open ? `${agentOf(open)} recommends ${fieldLabel(fieldValue(open, field))} · waiting for a person` : null;
  const di = rows.findIndex((p) => decides(p, field));
  if (!aiCanJudge && !rows.some(isAgent)) return null;
  if (di < 0) return open ? { state: 'ai_waiting', tip: openTip as string, waitingTip: null } : null;
  const d = rows[di];
  const who = by(names.nameOf(d.acceptedByUserId), d.acceptedAt);
  const waitingTip = open && when(open) > when(d) ? openTip : null;
  if (isAgent(d)) return { state: 'ai_approved', tip: `AI recommended · approved by ${who}`, waitingTip };

  // A person's entry: what AI value did it change or replace? Look back to the previous decision on this field.
  for (const p of rows.slice(di + 1)) {
    const decided = decides(p, field);
    if (isAgent(p) && (decided || p.status === 'superseded' || p.status === 'overridden')) {
      if (fieldValue(p, field) !== fieldValue(d, field)) {
        const tip = decided
          ? `Changed by ${who} · replaced ${agentOf(p)}'s ${fieldLabel(fieldValue(p, field))}`
          : `Changed by ${who} · ${agentOf(p)} recommended ${fieldLabel(fieldValue(p, field))}`;
        return { state: 'ai_overridden', tip, waitingTip };
      }
      break;
    }
    if (decided) break;
  }
  return { state: 'human', tip: `Human · set by ${who}`, waitingTip };
}

// ── An agent's own record of one recommendation (agent_decision_records) ────

/**
 * The mark on a decided recommendation in an agent's report: what became of
 * it, in the same five states. `reviewer` is the record's reviewed_by as a name
 * (the person who accepted it, or for a standing rule the administrator who set
 * the rule, migration 920); without it the tip names the date only. Rejected,
 * dismissed and expired recommendations decided nothing, so they carry no mark.
 */
export function decisionRecordMark(
  outcome: string | null | undefined,
  agent: string,
  proposed: string,
  at: Date | string | null | undefined,
  reviewer?: string | null,
): ValueMarkView | null {
  const on = day(at);
  const tail = on ? `, ${on}` : '';
  switch (outcome) {
    case 'accepted_as_proposed':
      return { state: 'ai_approved', tip: reviewer ? `AI recommended · approved by ${by(reviewer, at)}` : `AI recommended · approved${tail}`, waitingTip: null };
    case 'accepted_by_rule':
      return { state: 'ai_rule', tip: reviewer ? `AI decided${tail} · by the rule ${reviewer} set` : `AI decided · by the standing rule${tail}`, waitingTip: null };
    case 'accepted_with_change':
      return {
        state: 'ai_overridden',
        tip: reviewer ? `Changed by ${by(reviewer, at)} · ${agent} recommended ${proposed}` : `Changed before accepting${tail} · ${agent} recommended ${proposed}`,
        waitingTip: null,
      };
    case 'reversed':
      return { state: 'ai_overridden', tip: `Changed later${tail} · replaced ${agent}'s ${proposed}`, waitingTip: null };
    default:
      return null;
  }
}
